import sessionStore from 'client/Session/SessionStore';
import notifier from '@albert-io/notifier';
import {addGenericErrorToast} from '@albert-io/atomic';
import type {SupplementModelV1} from '@albert-io/models';
import type {List} from 'immutable';
import type {QuestionModelV3, SubjectModelV2} from 'resources/augmented/Question/QuestionModel.v3';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {getTranslationCoverage, getTranslationSettings} from './Translation.utils';
import {
  fetchTranslatedQuestion,
  fetchTranslatedSupplements,
  getStudentSchoolPermissionsWithoutAssignment,
  getTeacherTranslationPermissions,
  getStudentClassWithSchool,
  fetchQuestionSupplements,
  getAssignmentQuery
} from './Translation.queries';
import type {
  AlternateContentLanguage,
  TranslatedQuestion,
  TranslatedSupplement,
  TranslationCoverage
} from './Translation.types';
import {ALTERNATE_CONTENT_LANGUAGES} from './Translation.types';

interface QuestionTranslationState {
  translatedQuestionInfo: TranslatedQuestion | null;
  translatedSupplements: TranslatedSupplement[] | null;
  fetchingTranslations: boolean;
  translationCoverage: TranslationCoverage;
  hasTranslationAccess: boolean;
  isReady: boolean;
  translationSettings: {
    question: boolean;
    supplements: boolean;
  };
  showTranslations: boolean;
  pendingShowTranslations: boolean | null;
  useAssignmentSettings: boolean;
  assignmentSettingsAllowTranslation: boolean;
  questionId: string | null;
}

interface QuestionTranslationActions {
  fetchTranslation: (
    question: QuestionModelV3,
    subject: SubjectModelV2,
    language?: AlternateContentLanguage
  ) => Promise<void>;
  init: (assignmentId: string | null, isAdaptive?: boolean) => Promise<void>;
  onToggle: () => void;
  setShowTranslations: (showTranslations: boolean) => void;
  reset: () => void;

  // refreshTranslations(): Promise<void>;
}

const initialState: QuestionTranslationState = {
  translatedQuestionInfo: null,
  translatedSupplements: null,
  fetchingTranslations: false,
  translationCoverage: 'none',
  hasTranslationAccess: false,
  useAssignmentSettings: false,
  isReady: false,
  showTranslations: false,
  pendingShowTranslations: null,
  questionId: null,
  translationSettings: {
    question: false,
    supplements: false
  },
  assignmentSettingsAllowTranslation: false
};

export const useQuestionTranslationStore = create<
  QuestionTranslationState & QuestionTranslationActions
>()(
  devtools((set, get) => ({
    ...initialState,
    reset: () => {
      set({
        ...initialState,
        questionId: get().questionId,
        showTranslations: get().showTranslations
      });
    },
    init: async (assignmentId, isAdaptive = false) => {
      if (sessionStore.isStudent()) {
        const studentId = sessionStore.getUserId();
        let hasPermission = false;
        let useAssignmentSettings = false;
        if (isAdaptive || !assignmentId) {
          // In non assignment student view
          hasPermission = await getStudentSchoolPermissionsWithoutAssignment(studentId);
        } else if (assignmentId) {
          // In assignment view
          const classroom = await getStudentClassWithSchool(assignmentId, studentId);
          const school = classroom?.getSchool?.();
          hasPermission = school?.isAllowTranslations() || false;

          useAssignmentSettings = true; // only use assignment settings if in an assignment view
          // The JSON:API classroom/assignment models returned by the query layer are
          // dynamic; declare the runtime methods we reach for here and narrow defensively.
          const classroomWithId = classroom as
            | {getId?: () => string | undefined}
            | null
            | undefined;
          const classroomId = classroomWithId?.getId?.();
          const assignment = (await getAssignmentQuery(
            assignmentId,
            classroomId,
            studentId
          ).getResourcePromise()) as
            | {
                getIsAllowTranslationFromSettings?: (idsToSelect: {
                  classroomId?: string;
                  studentId?: string | null;
                }) => boolean;
              }
            | null
            | undefined;

          set({
            assignmentSettingsAllowTranslation:
              assignment?.getIsAllowTranslationFromSettings?.({classroomId, studentId}) || false
          });
        }

        set({
          isReady: true,
          hasTranslationAccess: hasPermission,
          useAssignmentSettings
        });
      } else if (sessionStore.isSchoolAdministrator() || sessionStore.isTeacher()) {
        let hasPermission = true;

        const teacherId = sessionStore.getUserId();
        hasPermission = await getTeacherTranslationPermissions(teacherId);

        set({
          isReady: true,
          hasTranslationAccess: hasPermission
        });
      } else {
        set({
          isReady: true,
          hasTranslationAccess: false
        });
      }
    },
    onToggle: () => {
      const currentStatus = get().showTranslations;
      set({
        showTranslations: !currentStatus
      });
    },
    setShowTranslations: (showTranslations) => {
      set({
        showTranslations,
        // Store intent to be consumed by the next fetchTranslation
        pendingShowTranslations: showTranslations
      });
    },
    fetchTranslation: async (question, subject, language = ALTERNATE_CONTENT_LANGUAGES[0]) => {
      if (get().isReady && !get().hasTranslationAccess) {
        // Don't fetch if no access to translation info
        return;
      }

      const previousQuestionId = get().questionId;
      const questionChanged =
        previousQuestionId !== null && question.getId() !== previousQuestionId;
      const pending = get().pendingShowTranslations;

      let nextShowTranslations = get().showTranslations;
      if (pending !== null) nextShowTranslations = pending;
      else if (questionChanged) nextShowTranslations = false;

      const fetchedQuestionId = question.getId();
      set({
        fetchingTranslations: true,
        questionId: fetchedQuestionId,
        pendingShowTranslations: null,
        showTranslations: nextShowTranslations
      });

      try {
        // fetchQuestionSupplements resolves a dynamic JSON:API resource list; narrow to the
        // concrete model list the translation utils expect.
        const supplements = (await fetchQuestionSupplements(
          fetchedQuestionId
        )) as List<SupplementModelV1>;

        const supplementIds = supplements.map((s) => s.getId()).toJS();

        const [{info}, translatedSupplements] = await Promise.all([
          fetchTranslatedQuestion(fetchedQuestionId, language),
          fetchTranslatedSupplements(supplementIds)
        ]);

        // Bail if the active question changed while this (now stale) fetch was in flight, so
        // we don't clobber the current question's translation state with the previous one's.
        if (get().questionId !== fetchedQuestionId) {
          return;
        }

        const translationCoverage = getTranslationCoverage(info, translatedSupplements, {
          question,
          subject,
          supplements
        });

        const translationSettings = getTranslationSettings({
          question,
          subject,
          supplements
        });
        set({
          translatedSupplements,
          translationCoverage, // TODO: change to match api field,
          // reflect this status on FE instead of just a can view toggle
          translatedQuestionInfo: info,
          fetchingTranslations: false,
          translationSettings: {
            question: translationSettings.question,
            supplements: translationSettings.supplement
          }
        });
      } catch (error) {
        notifier.notify(error as Error, {
          message: 'Failed to fetch question translations',
          tags: 'translations'
        });
        addGenericErrorToast();
        // Only clear the spinner if this is still the active fetch; a stale fetch must not
        // reset the flag for the question that superseded it.
        if (get().questionId === fetchedQuestionId) {
          set({fetchingTranslations: false});
        }
      }
    }
  }))
);
